import { clampFrameHeaderToSurface } from "./frame-window-gestures";

export type FocusWindowMode = "normal" | "maximized" | "left" | "right";

export interface FocusWindowBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface FocusWindowLayout {
  frameId: string;
  mode: FocusWindowMode;
  normalBox: FocusWindowBox;
}

/** A focus shows at most this many frames: the main one and three beside it. */
export const MAX_FOCUS_FRAMES = 4;

const NORMAL_MIN_W = 720;
const NORMAL_MIN_H = 520;
const NORMAL_MAX_W = 1100;
const NORMAL_MAX_H = 760;
const NORMAL_W_RATIO = 0.5;
const NORMAL_H_RATIO = 0.55;
/** The space a focus window keeps from the surface edge. */
export const FOCUS_WINDOW_MARGIN = 24;
const SNAP_ZONE_PX = 36;

function clampDimension(
  preferred: number,
  min: number,
  max: number,
  available: number,
) {
  const upper = Math.max(1, Math.min(max, available));
  const lower = Math.min(min, upper);
  return Math.min(Math.max(preferred, lower), upper);
}

export function roundBox(box: FocusWindowBox): FocusWindowBox {
  return {
    x: Math.round(box.x),
    y: Math.round(box.y),
    width: Math.round(box.width),
    height: Math.round(box.height),
  };
}

/** `box` moved and shrunk to lie inside `surface`. */
export function clampBoxToSurface(
  box: FocusWindowBox,
  surface: FocusWindowBox,
): FocusWindowBox {
  const width = Math.min(Math.max(1, box.width), surface.width);
  const height = Math.min(Math.max(1, box.height), surface.height);
  return {
    x: Math.min(Math.max(box.x, surface.x), surface.x + surface.width - width),
    y: Math.min(
      Math.max(box.y, surface.y),
      surface.y + surface.height - height,
    ),
    width,
    height,
  };
}

export function calculateFocusSurfaceBox(viewport: {
  width: number;
  height: number;
}): FocusWindowBox {
  return {
    x: 0,
    y: 0,
    width: Math.max(1, viewport.width),
    height: Math.max(1, viewport.height),
  };
}

export function calculateNormalFocusWindowBox(
  surface: FocusWindowBox,
): FocusWindowBox {
  const availableWidth = Math.max(1, surface.width - FOCUS_WINDOW_MARGIN * 2);
  const availableHeight = Math.max(1, surface.height - FOCUS_WINDOW_MARGIN * 2);
  const width = clampDimension(
    surface.width * NORMAL_W_RATIO,
    NORMAL_MIN_W,
    NORMAL_MAX_W,
    availableWidth,
  );
  const height = clampDimension(
    surface.height * NORMAL_H_RATIO,
    NORMAL_MIN_H,
    NORMAL_MAX_H,
    availableHeight,
  );
  return roundBox({
    x: surface.x + (surface.width - width) / 2,
    y: surface.y + (surface.height - height) / 2,
    width,
    height,
  });
}

export function calculateFocusWindowBox(
  mode: FocusWindowMode,
  surface: FocusWindowBox,
  normalBox = calculateNormalFocusWindowBox(surface),
): FocusWindowBox {
  if (mode === "normal")
    return roundBox(clampFrameHeaderToSurface(normalBox, surface));
  if (mode === "left") {
    return roundBox({
      x: surface.x,
      y: surface.y,
      width: surface.width / 2,
      height: surface.height,
    });
  }
  if (mode === "right") {
    return roundBox({
      x: surface.x + surface.width / 2,
      y: surface.y,
      width: surface.width / 2,
      height: surface.height,
    });
  }
  return roundBox(surface);
}

export function focusWindowModeForPoint(
  point: { x: number; y: number },
  surface: FocusWindowBox,
): Exclude<FocusWindowMode, "normal"> | null {
  if (point.y - surface.y <= SNAP_ZONE_PX) return "maximized";
  if (point.x - surface.x <= SNAP_ZONE_PX) return "left";
  if (surface.x + surface.width - point.x <= SNAP_ZONE_PX) return "right";
  return null;
}
